/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { propTypes } from './props';
import { Image as KonvaImage } from 'react-konva';
import Konva from 'konva';

export const CanvasImage = ({
    id,
    element,
    onClick,
    onDragEnd,
    onTransformEnd,
    onDragMove,
    setLoading,
}) => {
    const [loaded, setLoaded] = useState(false);
    const [imgEl, setImgEl] = useState(null);
    const imageNodeRef = useRef(null);

    const tintStrength = Number(element?.tintStrength ?? 0);
    const hasTint = tintStrength > 0;

    const { red, green, blue } = useMemo(() => {
        const hex = typeof element?.tintColor === 'string' ? element.tintColor.trim() : '#000000';
        const safeHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? hex : '#000000';
        const normalized =
            safeHex.length === 4
                ? `#${safeHex[1]}${safeHex[1]}${safeHex[2]}${safeHex[2]}${safeHex[3]}${safeHex[3]}`
                : safeHex;

        const r = parseInt(normalized.slice(1, 3), 16);
        const g = parseInt(normalized.slice(3, 5), 16);
        const b = parseInt(normalized.slice(5, 7), 16);

        const lerp = (start, end, t) => start + (end - start) * Math.min(Math.max(t, 0), 1);

        // At 0: keep original grayscale; at 1: fully tint to the picked color.
        return {
            red: Math.round(lerp(255, r, tintStrength)),
            green: Math.round(lerp(255, g, tintStrength)),
            blue: Math.round(lerp(255, b, tintStrength)),
        };
    }, [element?.tintColor, tintStrength]);

    useEffect(() => {
        let cancelled = false;
        let objectUrl;

        async function load() {
            try {
                setLoading(true);
                // Force a network fetch with CORS, ignoring any stale cached copy
                const res = await fetch(element?.src, { mode: 'cors', cache: 'reload' });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                objectUrl = URL.createObjectURL(blob);

                const img = new window.Image();
                // No need to set crossOrigin for blob URLs (they're same-origin to your page)
                img.onload = () => {
                    if (!cancelled) {
                        setImgEl(img);
                        setLoaded(true);
                    }
                    URL.revokeObjectURL(objectUrl);
                };
                img.src = objectUrl;
            } catch (err) {
                console.error('Failed to load image for canvas:', err);
            } finally {
                setLoading(false);
            }
        }

        setLoaded(false);
        setImgEl(null);
        if (element?.src) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [element?.src]);

    useEffect(() => {
        if (!imageNodeRef?.current) return;
        if (hasTint) {
            imageNodeRef.current.cache();
            imageNodeRef.current.getLayer()?.batchDraw();
        } else {
            imageNodeRef.current.clearCache();
            imageNodeRef.current.getLayer()?.batchDraw();
        }
    }, [hasTint, red, green, blue]);

    if (!loaded || !imgEl) return null;

    return (
        <KonvaImage
            ref={imageNodeRef}
            id={id}
            image={imgEl}
            {...element}
            width={element?.width}
            height={element?.height}
            filters={hasTint ? [Konva.Filters.Grayscale, Konva.Filters.RGB] : undefined}
            red={hasTint ? red : undefined}
            green={hasTint ? green : undefined}
            blue={hasTint ? blue : undefined}
            onClick={() => onClick && onClick()}
            onDragEnd={e => onDragEnd && onDragEnd(e)}
            onDragMove={onDragMove}
            onTransformEnd={e => onTransformEnd && onTransformEnd(e)}
            strokeScaleEnabled={false}
        />
    );
};

CanvasImage.propTypes = propTypes;
